import { Rider } from "./extras";

// "The butterfly wing": a crayon wing on a storybook page → the girl and her butterfly lift off → a ride among butterflies → the wing becomes the painting.
export const butterfly = {
  slug: "the-butterfly-wing",
  duration: 27,
  extra: Rider,
  music: "butterfly-glockenspiel",
  captionTail: "I never wanted to catch them, only to see them around.",
  stages: [
    { id: "page", at: 0, hint: "an art-class page" },
    { id: "storybook", at: 6, hint: "the storybook" },
    { id: "ride", at: 10.5, hint: "tap near a butterfly" },
    { id: "held", at: 20.5, hint: "" },
  ],
  touch: { kind: "flutter", from: 11, until: 20.5 },
  line: { text: "Butterflies are the universe's way of telling you: you are surrounded by joy.", from: 13, to: 26.5, pos: "low" },
  ambient: { scene: "summer", until: 99, then: [] },
  look: { start: -0.05, lift: [0, 1], end: -0.05, yaw: [[0, 0], [12, 0], [15, 0.5], [18, -0.5], [20.5, 0], [27, 0]] },
  calm: { duration: 12, fade: [4, 8.5], line: [5.5, 11.5], backdrop: "storybook", pos: "low" },
  speed: [[0, 0], [9, 0], [11, 1], [20, 1], [22, 0.2], [27, 0.2]],
  frag: `
uniform sampler2D uMap;
uniform vec4 uCrop;
uniform float uT, uPhase, uAspect, uTileH, uRadius, uCy, uSurface;
uniform vec4 uMark[8];
varying vec3 vPos;
${"__NOISE__"}
float hash2(vec2 p){ return fract(sin(dot(p, vec2(41.3, 289.1))) * 43758.5453); }
vec3 tap(vec2 uv){
  vec2 m = 1.0 - abs(1.0 - mod(uv, 2.0));
  return texture2D(uMap, mix(uCrop.xy, uCrop.zw, m)).rgb;
}
float ell(vec2 p, vec2 c, vec2 r, float a){ p -= c; p = mat2(cos(a), -sin(a), sin(a), cos(a)) * p; return length(p / r); }
// a butterfly, wings beating: x squeezes as the wings rise and fall
float bfly(vec2 p, float s, float flap, out float edge){
  p /= s;
  float sq = mix(1.0, 0.18, 0.5 + 0.5 * sin(flap));
  vec2 q = vec2(abs(p.x) / sq, p.y);
  float d = min(ell(q, vec2(0.5, 0.18), vec2(0.5, 0.34), -0.4), ell(q, vec2(0.34, -0.24), vec2(0.3, 0.26), 0.45));
  edge = smoothstep(0.8, 0.95, d);
  float body = step(length(vec2(p.x / 0.05, p.y / 0.42)), 1.0);
  return max(1.0 - smoothstep(0.94, 1.0, d), body);
}
vec3 wingCol(float h){
  return h < 0.17 ? vec3(0.95, 0.5, 0.1) : h < 0.33 ? vec3(0.2, 0.45, 0.95) : h < 0.5 ? vec3(0.98, 0.86, 0.25) : h < 0.66 ? vec3(0.97, 0.96, 0.92) : h < 0.82 ? vec3(0.92, 0.45, 0.68) : vec3(0.4, 0.75, 0.45);
}
void main(){
  float n = snoise(vPos * 0.6) * 0.5 + 0.5;
  float n2 = snoise(vPos * 3.0 + 4.0) * 0.5 + 0.5;
  float th = atan(vPos.x, -vPos.z);
  float world = smoothstep(9.0, 11.5, uT);
  float settle = smoothstep(20.5, 25.5, uT);
  vec3 col;
  vec2 local;
  vec3 paper = vec3(0.98, 0.96, 0.9) * (0.96 + 0.05 * n2);
  if (uSurface < 0.5) {
    float X = th * uRadius;
    float y = vPos.y;
    // the storybook: two pages turn across the room
    vec3 pg = paper;
    float turns = clamp((uT - 6.0) / 1.5, 0.0, 2.0);
    float edgeTh = mix(3.2, -3.2, fract(turns));
    float turned = step(edgeTh, th) * step(0.001, turns) + step(1.0, turns);
    pg = mix(pg, paper * vec3(0.99, 0.95, 0.88) - 0.03 * sin(y * 6.0), clamp(turned, 0.0, 1.0));
    pg -= 0.12 * exp(-pow((th - edgeTh) * 6.0, 2.0)) * step(0.001, turns) * step(turns, 1.999);
    // the ride: sky, clouds, a meadow and a river streaming past
    float sx = X + uPhase * 2.2;
    vec3 sky = mix(vec3(0.72, 0.86, 0.98), vec3(0.42, 0.64, 0.95), smoothstep(1.0, 8.0, y));
    float cloud = smoothstep(0.55, 0.75, snoise(vec3(sx * 0.18, y * 0.5, 1.0)) * 0.5 + 0.5) * smoothstep(2.5, 4.5, y);
    sky = mix(sky, vec3(0.98), cloud * 0.85);
    float hz = 1.1 + 0.15 * sin(sx * 0.4);
    vec3 meadow = mix(vec3(0.36, 0.62, 0.26), vec3(0.5, 0.72, 0.3), n2);
    float river = 1.0 - smoothstep(0.08, 0.14, abs(y - 0.55 - 0.25 * sin(sx * 0.5)));
    meadow = mix(meadow, vec3(0.45, 0.7, 0.9) + 0.08 * sin(sx * 9.0 + uT * 3.0), river);
    vec2 fc = floor(vec2(sx, y) / 0.22);
    vec2 fq = vec2(sx, y) - (fc + 0.5) * 0.22;
    float fr = 0.055 * (0.35 + 0.65 * pow(abs(cos(2.5 * atan(fq.y, fq.x))), 0.7));
    meadow = mix(meadow, wingCol(hash2(fc)), step(0.86, hash2(fc + 1.0)) * step(length(fq), fr));
    vec3 ride = y < hz ? meadow : sky;
    // butterflies of every kind all around, wings beating
    vec2 bq = vec2(sx * 0.9 + sin(uT * 0.6 + y) * 0.2, y + 0.25 * sin(uT * 0.8 + sx));
    vec2 bc = floor(bq / 1.1);
    float bh = hash2(bc);
    if (bh > 0.45 && y > 1.2) {
      float e;
      float k = bfly(bq - (bc + 0.5) * 1.1, 0.22 + 0.1 * hash2(bc + 5.0), uT * (7.0 + 4.0 * bh) + bh * 30.0, e);
      vec3 wc = mix(wingCol(hash2(bc + 2.0)), vec3(0.08), e * 0.85);
      ride = mix(ride, wc, k);
    }
    // the one you reached for: it circles close, then lands softly
    for (int i = 0; i < 8; i++){
      vec4 m = uMark[i];
      if (m.w <= 0.0) continue;
      float age = uT - m.w;
      float rr = 0.7 * (1.0 - smoothstep(0.0, 3.0, age));
      float an = age * 2.6;
      vec2 c = vec2(atan(m.x, -m.z) * uRadius + cos(an) * rr, m.y + sin(an) * rr * 0.6);
      float e;
      float k = bfly(vec2(X, y) - c, 0.26, uT * mix(9.0, 2.0, smoothstep(2.5, 3.5, age)), e);
      ride = mix(ride, mix(wingCol(fract(m.w * 3.7)), vec3(0.08), e * 0.85), k);
    }
    col = mix(pg, ride, world);
    local = vec2(X, y - uCy);
  } else if (uSurface < 1.5) {
    vec2 xz = vPos.xz + vec2(0.0, -uPhase * 2.2);
    vec3 meadow = mix(vec3(0.34, 0.6, 0.24), vec3(0.48, 0.7, 0.3), snoise(vec3(xz * 0.8, 2.0)) * 0.5 + 0.5);
    float river = 1.0 - smoothstep(0.5, 0.7, abs(xz.x - 1.2 * sin(xz.y * 0.3)));
    meadow = mix(meadow, vec3(0.45, 0.7, 0.9) + 0.06 * sin(xz.y * 6.0 + uT * 2.0), river);
    col = mix(paper * 0.95, meadow, world);
    local = vPos.xz;
  } else {
    col = mix(paper, vec3(0.5, 0.7, 0.96), world);
    local = vPos.xz;
  }
  vec2 tile = vec2(uTileH * uAspect, uTileH);
  vec3 paint = tap(local / tile + 0.5);
  float drip = uSurface < 0.5 ? (10.0 - vPos.y) / 10.0 : 0.5 + length(vPos.xz) / (uRadius * 3.0);
  col = mix(col, paint, smoothstep(0.0, 0.25, settle * 1.3 - drip * 0.6 - n * 0.25));
  gl_FragColor = vec4(clamp(col, 0.0, 1.0), 1.0);
  #include <colorspace_fragment>
}`,
};
